
/**
 * Core onboarding utility functions
 * This serves as the main entry point for onboarding functionality
 */

import { getLocalOnboardingStatus, setLocalOnboardingStatus } from './storageUtils';
import { checkDatabaseOnboardingStatus, updateDatabaseOnboardingStatus } from './databaseUtils';
import { GLOBAL_ONBOARDING_FLAG, getChecklistStateKey } from './constants';

/**
 * Check if onboarding is completed for a user
 * This is the definitive way to check onboarding status
 */
export const isOnboardingCompletedV2 = async (userId?: string): Promise<boolean> => {
  try {
    // If we don't have a userId, onboarding can't be completed
    if (!userId) return false;
    
    // Check local storage first (fastest)
    if (getLocalOnboardingStatus(userId)) {
      return true;
    }
    
    // Check database status (single source of truth)
    const isCompletedInDatabase = await checkDatabaseOnboardingStatus(userId);
    
    if (isCompletedInDatabase) {
      // Sync with local storage for future checks
      setLocalOnboardingStatus(userId, true);
      return true;
    }
    
    return false;
  } catch (err) {
    console.error("[ONBOARDING] Error checking onboarding status:", err);
    // Fallback to local storage on error
    return getLocalOnboardingStatus(userId);
  }
};

/**
 * Mark onboarding as completed for a user
 * This permanently sets all flags needed for onboarding completion
 */
export const markOnboardingAsCompleted = async (userId?: string): Promise<void> => {
  try {
    console.log("[ONBOARDING] Marking onboarding as permanently completed");
    
    // Set local storage flag
    setLocalOnboardingStatus(userId, true);
    
    // Update database (single source of truth)
    await updateDatabaseOnboardingStatus(userId, true);
  } catch (err) {
    console.error("[ONBOARDING] Error marking onboarding as completed:", err);
  }
};

/**
 * Check if onboarding should be visible for the current user session
 */
export const shouldShowOnboarding = async (userId?: string): Promise<boolean> => {
  // If no user ID, never show onboarding
  if (!userId) return false;
  
  try {
    // Get onboarding status
    const isCompleted = await isOnboardingCompletedV2(userId);
    
    // Only show if not completed
    return !isCompleted;
  } catch (err) {
    console.error("[ONBOARDING] Error checking if onboarding should be shown:", err);
    return false;
  }
};

/**
 * Reset onboarding status (for testing purposes only)
 */
export const resetOnboardingStatus = async (userId?: string): Promise<void> => {
  try {
    // Reset local storage
    localStorage.removeItem(GLOBAL_ONBOARDING_FLAG);
    
    if (userId) {
      localStorage.removeItem(`onboarding-completed-${userId}`);
    }
    
    // Reset database
    await updateDatabaseOnboardingStatus(userId, false);
  } catch (err) {
    console.error("[ONBOARDING] Error resetting onboarding status:", err);
  }
};

// Re-export constants for backward compatibility
export { GLOBAL_ONBOARDING_FLAG, getChecklistStateKey };
